import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthResponseData } from '../interfaces/auth.types';
import { User } from '../interfaces/user.types';
import { logout as logoutService } from '../services/authService';

interface AuthContextType {
    isAuthenticated: boolean;
    user: User | null;
    accessToken: string | null;
    isLoading: boolean;
    login: (loginData: AuthResponseData) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        try {
            const storedToken = localStorage.getItem('accessToken');
            const storedUser = localStorage.getItem('user');

            if (storedToken && storedUser) {
                setAccessToken(storedToken);
                setUser(JSON.parse(storedUser));
            }
        } catch (error) {
            console.error("Error al cargar estado de autenticación:", error);
            localStorage.clear();
        } finally {
            setIsLoading(false);
        }
    }, []);

    const login = (loginData: AuthResponseData) => {
        const { user: loggedInUser, accessToken: newAccessToken, refreshToken } = loginData;
        
        setUser(loggedInUser);
        setAccessToken(newAccessToken);

        localStorage.setItem('user', JSON.stringify(loggedInUser));
        localStorage.setItem('accessToken', newAccessToken);
        localStorage.setItem('refreshToken', refreshToken);
    };

    const logout = () => {
        const refreshToken = localStorage.getItem('refreshToken');
        
        // Llamada al servicio para invalidar el token en el backend
        if (refreshToken) {
            logoutService({ refreshToken }).catch(err => {
                console.error("No se pudo invalidar el token en el backend:", err);
            });
        }
        
        setUser(null);
        setAccessToken(null);

        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        // Redirigir al login
        navigate('/login', { state: { from: location }, replace: true });
    };

    const value = {
        isAuthenticated: !!accessToken,
        user,
        accessToken,
        isLoading,
        login,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {!isLoading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth debe ser usado dentro de un AuthProvider');
    }
    return context;
};